/**
 * Seção 7 da especificação. Individual por usuário, sem perfis.
 *
 * As chaves ficam aqui e em nenhum outro lugar. Chave escrita à mão em dois
 * arquivos diferentes é como se abre um buraco de permissão sem ninguém ver.
 *
 * **Tudo é permissão (ADR 0038).** Pedido do Mike: o que era só do proprietário
 * (importar e exportar, API oficial, senha dos outros, apagar logs), o que era
 * regra escondida (quem vê todos os números, quem recebe aviso de supervisão,
 * quem vê os avisos do sistema) e a Configuração, que era uma chave só, viraram
 * chaves desta lista, para ele decidir pessoa por pessoa. O proprietário passa
 * por todas e não pode ser limitado: é quem destrava quem se trancar para fora.
 */

export const TELAS = [
  'bate_papo',
  'leads',
  'calendario',
  'estatisticas',
  'logs',
  'configuracao',
  'anuncios',
  'materiais',
  'painel',
  'marketing',
] as const

export const ACOES = ['ver', 'criar', 'editar', 'excluir'] as const

/**
 * Quais ações existem de verdade em cada tela.
 *
 * Antes as chaves saíam do produto TELAS × ACOES, e a tela de permissões
 * mostrava as 24 combinações. Doze delas o servidor nunca consultava —
 * "criar estatística", "excluir log" — e ficavam ali parecendo controle.
 * Pior: `bate_papo.criar` e `bate_papo.editar` estavam nesse grupo por
 * engano, então quem recebia só "ver" no bate-papo mandava mensagem para o
 * cliente do mesmo jeito. Chave que ninguém confere é pior que chave que não
 * existe, porque passa a sensação de que o acesso está limitado.
 *
 * Regra para quem for mexer: acrescente a chave aqui **e** no `preHandler` da
 * rota, no mesmo commit.
 */
export const ACOES_POR_TELA = {
  bate_papo: ['ver', 'criar', 'editar'],
  leads: ['ver', 'criar', 'editar', 'excluir'],
  calendario: ['ver', 'criar', 'editar', 'excluir'],
  estatisticas: ['ver'],
  logs: ['ver'],
  /*
    Só "ver" (abrir a Configuração e olhar). Mudar é por parte, em chaves
    próprias (`configuracao.setores`, `configuracao.funis`...): quem cuida das
    respostas rápidas não precisa poder apagar um funil (ADR 0038).
  */
  configuracao: ['ver'],
  /*
    `editar` é qualificar ou desqualificar. Qualificar lead na conversa pede a
    mesma chave da tela, e não a do bate-papo: a mesma ação não pode ter duas
    portas com fechaduras diferentes, senão quem foi barrado numa tela faz pela outra.
  */
  anuncios: ['ver', 'editar'],
  /*
    `ver` é a biblioteca aberta dentro da conversa: baixar no computador e
    mandar para o cliente. `editar` é mexer no acervo — criar pasta, subir
    arquivo, apagar. São chaves separadas porque quem vende usa a primeira o
    dia inteiro e nunca deveria precisar da segunda: material apagado some da
    mão de todo mundo ao mesmo tempo.
  */
  materiais: ['ver', 'editar'],
  /*
    `ver` é abrir o painel e enxergar o que está parado com cada pessoa.
    `editar` é dar baixa e limpar o histórico.

    São separadas porque quem confere e quem apaga não precisam ser a mesma
    pessoa: dar baixa sem motivo e apagar o histórico logo em seguida é o
    caminho exato para o painel deixar de servir para o que existe.
  */
  painel: ['ver', 'editar'],
  /*
    FaroEscolar Marketing (ADR 0057). `ver` abre o calendário de posts;
    `criar` e `editar` montam o post; `excluir` apaga. Agendar sem passar por
    aprovação é `marketing.aprovar`: quem não tem manda para aprovação.
  */
  marketing: ['ver', 'criar', 'editar', 'excluir'],
} as const satisfies Record<Tela, readonly Acao[]>

export const PERMISSOES_ESPECIAIS = [
  // Bate-papo: quais conversas vê
  'bate_papo.fila',
  'bate_papo.todas',
  // Bate-papo: o que faz dentro da conversa
  'bate_papo.iniciar',
  'bate_papo.arquivos',
  'bate_papo.nota_interna',
  'bate_papo.editar_mensagem',
  'bate_papo.apagar_mensagem',
  'encerrar_chamado',
  'transferir_chamado',
  'bate_papo.tirar_apoio',
  // Mensagem agendada
  'agendar_mensagem',
  'agendamentos.outros',
  // Leads
  'leads.mover',
  'leads.fechar',
  'leads.valor',
  'leads.em_massa',
  // Calendário
  'calendario.outros',
  'calendario.para_outros',
  // Estatísticas
  'estatisticas.metas',
  'estatisticas.atendimento',
  'estatisticas.vendas',
  'estatisticas.anuncios',
  'metas.definir',
  // Configuração, parte por parte
  'configuracao.setores',
  'configuracao.numeros',
  'configuracao.campos',
  'configuracao.funis',
  'configuracao.etiquetas',
  'configuracao.respostas',
  'configuracao.sistema',
  'api_oficial',
  // Importar e exportar
  'contatos.importar',
  'contatos.exportar',
  'empresas.importar',
  'empresas.exportar',
  'estatisticas.exportar',
  'historico.importar',
  // Acesso aos dados
  'ver_telefone',
  'ver_base_completa',
  'numeros.todos',
  // Administração
  'gerenciar_usuarios',
  'usuarios.senha',
  'supervisao.receber',
  'avisos.fora',
  'avisos.ver',
  'logs.apagar',
  'gerenciar_chatbot',
  // Marketing
  'marketing.aprovar',
  'marketing.perfis',
] as const

export type Tela = (typeof TELAS)[number]
export type Acao = (typeof ACOES)[number]
export type PermissaoEspecial = (typeof PERMISSOES_ESPECIAIS)[number]
/**
 * Só as combinações que existem de verdade: `configuracao.editar` deixou de
 * existir (ADR 0038), e com o tipo largo, de toda tela com toda ação, uma
 * referência esquecida a ela continuava compilando e falhava calada.
 */
export type ChaveTela = {
  [T in Tela]: `${T}.${(typeof ACOES_POR_TELA)[T][number]}`
}[Tela]
export type ChavePermissao = ChaveTela | PermissaoEspecial

export function chaveDeTela(tela: Tela, acao: Acao): ChaveTela {
  return `${tela}.${acao}` as ChaveTela
}

export const CHAVES_TELA: ChaveTela[] = TELAS.flatMap((tela) =>
  ACOES_POR_TELA[tela].map((acao) => chaveDeTela(tela, acao)),
)

export const CHAVES_PERMISSAO: ChavePermissao[] = [...CHAVES_TELA, ...PERMISSOES_ESPECIAIS]

export const ROTULO_TELA: Record<Tela, string> = {
  bate_papo: 'Bate-papo',
  leads: 'Leads',
  calendario: 'Calendário',
  estatisticas: 'Estatísticas',
  logs: 'Logs',
  configuracao: 'Configuração',
  anuncios: 'Anúncios',
  materiais: 'Materiais',
  painel: 'Painel do admin',
  marketing: 'Marketing',
}

export const ROTULO_ACAO: Record<Acao, string> = {
  ver: 'Ver',
  criar: 'Criar',
  editar: 'Editar',
  excluir: 'Excluir',
}

export const ROTULO_ESPECIAL: Record<PermissaoEspecial, string> = {
  'bate_papo.fila': 'Ver a fila',
  'bate_papo.todas': 'Ver conversas de outras pessoas',
  'bate_papo.iniciar': 'Iniciar conversa nova',
  'bate_papo.arquivos': 'Enviar áudio, foto, vídeo e arquivo',
  'bate_papo.nota_interna': 'Escrever nota interna',
  'bate_papo.editar_mensagem': 'Editar mensagem enviada',
  'bate_papo.apagar_mensagem': 'Apagar mensagem enviada',
  encerrar_chamado: 'Encerrar conversa',
  transferir_chamado: 'Transferir conversa',
  'bate_papo.tirar_apoio': 'Tirar apoio de conversa de outra pessoa',
  agendar_mensagem: 'Agendar mensagem',
  'agendamentos.outros': 'Mexer em mensagens agendadas por outras pessoas',
  'leads.mover': 'Mover negociação de etapa',
  'leads.fechar': 'Marcar ganho e perda',
  'leads.valor': 'Mudar o valor da negociação',
  'leads.em_massa': 'Ações em massa',
  'calendario.outros': 'Ver tarefas de outras pessoas',
  'calendario.para_outros': 'Criar e mudar tarefa de outra pessoa',
  'estatisticas.metas': 'Aba Metas e equipe',
  'estatisticas.atendimento': 'Aba Atendimento',
  'estatisticas.vendas': 'Aba Vendas',
  'estatisticas.anuncios': 'Aba Anúncios',
  'metas.definir': 'Definir e excluir metas',
  'configuracao.setores': 'Mudar setores',
  'configuracao.numeros': 'Mudar números de WhatsApp',
  'configuracao.campos': 'Mudar campos',
  'configuracao.funis': 'Mudar funis e etapas',
  'configuracao.etiquetas': 'Mudar etiquetas e motivos de perda',
  'configuracao.respostas': 'Mudar respostas rápidas da equipe',
  'configuracao.sistema': 'Mudar as regras do sistema',
  api_oficial: 'API oficial do WhatsApp',
  'contatos.importar': 'Importar contatos',
  'contatos.exportar': 'Exportar contatos',
  'empresas.importar': 'Importar empresas',
  'empresas.exportar': 'Exportar empresas',
  'estatisticas.exportar': 'Exportar o Excel das Estatísticas',
  'historico.importar': 'Importar histórico do WhatsApp',
  ver_telefone: 'Ver telefone completo',
  ver_base_completa: 'Ver a base inteira',
  'numeros.todos': 'Ver todos os números de WhatsApp',
  gerenciar_usuarios: 'Gerenciar usuários e permissões',
  'usuarios.senha': 'Redefinir a senha de outra pessoa',
  'supervisao.receber': 'Receber avisos de supervisão',
  'avisos.fora': 'Receber mensagens de quem está pausado',
  'avisos.ver': 'Ver e resolver os avisos do sistema',
  'logs.apagar': 'Apagar logs',
  gerenciar_chatbot: 'Gerenciar chatbot',
  'marketing.aprovar': 'Aprovar e reprovar posts',
  'marketing.perfis': 'Cadastrar marcas e perfis de redes',
}

/**
 * O que cada permissão especial significa na prática.
 *
 * O rótulo sozinho não basta: "ver a base inteira" não deixa claro que, sem
 * ela, a pessoa enxerga só a própria carteira. Quem está configurando precisa
 * saber o que está tirando, não só o que está dando.
 */
export const DESCRICAO_ESPECIAL: Record<PermissaoEspecial, string> = {
  'bate_papo.fila':
    'As conversas sem dono, esperando alguém pegar. Desmarcado, a aba Fila some e a pessoa só vê as conversas que já são dela.',
  'bate_papo.todas':
    'As conversas que estão com outras pessoas, na aba Todas. Desmarcado, a pessoa vê só as dela, as em que está de apoio e (se puder) a fila.',
  'bate_papo.iniciar':
    'O botão + do bate-papo: abrir conversa com um número ou contato, mesmo que o cliente não tenha escrito.',
  'bate_papo.arquivos':
    'Mandar áudio gravado, foto, vídeo, documento e os materiais da biblioteca. Desmarcado, a pessoa manda só texto.',
  'bate_papo.nota_interna': 'Recado da equipe dentro da conversa, que o cliente não vê.',
  'bate_papo.editar_mensagem': 'Corrigir uma mensagem que já foi para o cliente.',
  'bate_papo.apagar_mensagem': 'Apagar para todos uma mensagem que já foi para o cliente.',
  encerrar_chamado: 'Fechar a conversa, escolhendo se o contato fica ou não na carteira.',
  transferir_chamado: 'Passar a conversa para outra pessoa ou para outro setor.',
  'bate_papo.tirar_apoio':
    'Tirar quem está de apoio numa conversa que é de outra pessoa. Sem isto, só o responsável pela conversa tira o apoio.',
  agendar_mensagem: 'Programar envio para depois, inclusive em lote.',
  'agendamentos.outros':
    'Ver, mudar e cancelar mensagens que outras pessoas agendaram. Desmarcado, a pessoa mexe só nas que ela mesma agendou.',
  'leads.mover': 'Arrastar o card de uma etapa para outra no funil.',
  'leads.fechar':
    'Levar o card para ganho ou perda, e reabrir negociação fechada. É o que gera venda e entra no faturamento.',
  'leads.valor': 'Mudar o valor estimado de uma negociação aberta.',
  'leads.em_massa':
    'Marcar vários de uma vez e transferir o dono, devolver à fila ou etiquetar todos, inclusive o "Marcar tudo" dos Contatos.',
  'calendario.outros':
    'Ver as tarefas da equipe toda no calendário. Desmarcado, a pessoa vê só as dela.',
  'calendario.para_outros':
    'Criar tarefa para outra pessoa e mudar ou excluir tarefa que é de outra pessoa.',
  'estatisticas.metas': 'Metas e o resultado de cada vendedor.',
  'estatisticas.atendimento': 'Conversas, tempo de resposta, setores e quando o cliente escreve.',
  'estatisticas.vendas': 'Ganhos, perdas, ticket médio e etapas.',
  'estatisticas.anuncios': 'Leads que chegaram por anúncio e quantos foram qualificados.',
  'metas.definir':
    'Criar e excluir meta de valor ou de leads, da equipe ou de uma pessoa.',
  'configuracao.setores': 'Criar, renomear e desativar setores.',
  'configuracao.numeros':
    'Conectar número pelo QR, renomear, desconectar, excluir e escolher quem vê cada número.',
  'configuracao.campos': 'Os campos extras da ficha do contato.',
  'configuracao.funis': 'Criar funil e mudar as etapas.',
  'configuracao.etiquetas': 'As etiquetas e os motivos de perda.',
  'configuracao.respostas':
    'As respostas rápidas da equipe toda. As pessoais, cada um sempre cria e muda as suas.',
  'configuracao.sistema':
    'Expediente, prazos, encerramento por inatividade, fuso e as outras regras da aba Sistema.',
  api_oficial: 'Credenciais da Meta, modelos aprovados e preços, na aba Números.',
  'contatos.importar': 'Subir planilha de contatos para dentro do CRM.',
  'contatos.exportar': 'Baixar a base de contatos em planilha.',
  'empresas.importar':
    'Subir planilha de empresas para dentro do CRM. Os contatos de cada empresa passam para o dono dela.',
  'empresas.exportar': 'Baixar as empresas em planilha.',
  'estatisticas.exportar':
    'Baixar o Excel das Estatísticas. Sai só com as abas que a pessoa pode ver.',
  'historico.importar': 'Trazer as conversas antigas do WhatsApp para dentro do CRM.',
  ver_telefone:
    'Desmarcado, o número aparece como 31*****-6935 em tudo: tela, relatório, impressão e exportação. A busca por telefone também para de funcionar.',
  ver_base_completa:
    'Desmarcado, a pessoa vê só os contatos da própria carteira em Leads e Estatísticas. O bate-papo segue as permissões da aba Bate-papo.',
  'numeros.todos':
    'Ver todos os números de WhatsApp da empresa. Desmarcado, a pessoa vê só os números liberados para ela (aba Números e setores).',
  gerenciar_usuarios:
    'Criar, editar e desativar usuários, e mexer nas permissões dos outros, não nas próprias. É a permissão mais forte que existe fora do proprietário.',
  'usuarios.senha':
    'Trocar a senha de outra pessoa. A sessão dela cai na hora. A senha do proprietário só ele mesmo troca.',
  'supervisao.receber':
    'Receber no sino os avisos de conversa sem resposta e de fila parada, da equipe toda.',
  'avisos.fora':
    'Aviso no sino quando um cliente escreve para alguém Pausado ou Offline, e quando um cliente fica na fila porque ninguém do setor está Online.',
  'avisos.ver':
    'A aba Avisos da Configuração: peça parada, número fora do ar, arquivo que não baixou, envio que falhou.',
  'logs.apagar': 'Apagar o histórico de quem fez o quê. Não tem volta.',
  gerenciar_chatbot: 'Criar e publicar fluxos do chatbot, que respondem sozinhos ao cliente.',
  'marketing.aprovar':
    'Agendar post direto e aprovar ou reprovar o post de quem não pode. Desmarcado, o "Agendar" da pessoa vira "Enviar para aprovação".',
  'marketing.perfis':
    'Criar e mudar as marcas e os perfis de Instagram, Facebook e das outras redes em que os posts saem.',
}

/**
 * As abas da janela de permissões, por assunto, e as seções dentro de cada uma.
 *
 * Cada aba junta as telas e as permissões especiais do mesmo assunto:
 * "Encerrar conversa" mora com o Bate-papo, porque é dentro da conversa que
 * ela vale. Separadas em "telas" e "especiais", como eram no começo, a pessoa
 * configurava o bate-papo lá em cima e tinha de lembrar de descer até o fim da
 * lista para decidir se o vendedor encerra conversa.
 *
 * Toda tela e toda permissão especial precisam estar em exatamente uma aba: o
 * teste `permissoes-por-assunto` confere. Uma chave nova que ficasse de fora
 * simplesmente não apareceria na janela, e ninguém conseguiria dá-la.
 */
export const ASSUNTOS_DE_PERMISSAO: {
  id: string
  titulo: string
  descricao: string
  telas: Tela[]
  /**
   * `requer`: a tela sem a qual as caixas da seção não têm onde valer. Sem o
   * "Ver" do Bate-papo, "ver a fila" não tem bate-papo para acontecer; a
   * janela deixa a seção desligada até o "Ver" ser marcado.
   */
  secoes: { titulo: string; chaves: PermissaoEspecial[]; requer?: ChaveTela }[]
}[] = [
  {
    id: 'bate_papo',
    titulo: 'Bate-papo',
    descricao: 'As conversas com o cliente: quais a pessoa vê e o que faz dentro delas.',
    telas: ['bate_papo'],
    secoes: [
      {
        titulo: 'Quais conversas vê',
        chaves: ['bate_papo.fila', 'bate_papo.todas'],
        requer: 'bate_papo.ver',
      },
      {
        titulo: 'Dentro da conversa',
        chaves: [
          'bate_papo.iniciar',
          'bate_papo.arquivos',
          'bate_papo.nota_interna',
          'bate_papo.editar_mensagem',
          'bate_papo.apagar_mensagem',
          'encerrar_chamado',
          'transferir_chamado',
          'bate_papo.tirar_apoio',
        ],
        requer: 'bate_papo.ver',
      },
      {
        titulo: 'Mensagem agendada',
        chaves: ['agendar_mensagem', 'agendamentos.outros'],
        requer: 'bate_papo.ver',
      },
    ],
  },
  {
    id: 'leads',
    titulo: 'Leads e contatos',
    descricao: 'O funil, os contatos e as empresas.',
    telas: ['leads'],
    secoes: [
      {
        titulo: 'No funil',
        chaves: ['leads.mover', 'leads.fechar', 'leads.valor', 'leads.em_massa'],
        requer: 'leads.ver',
      },
    ],
  },
  {
    id: 'vendas',
    titulo: 'Anúncios',
    descricao: 'Os leads que chegam por anúncio.',
    telas: ['anuncios'],
    secoes: [],
  },
  {
    id: 'conteudo',
    titulo: 'Materiais',
    descricao: 'A biblioteca de fotos, vídeos e PDFs para mandar na conversa.',
    telas: ['materiais'],
    secoes: [],
  },
  {
    id: 'agenda',
    titulo: 'Calendário',
    descricao: 'As tarefas: as da pessoa e as da equipe.',
    telas: ['calendario'],
    secoes: [
      {
        titulo: 'Tarefas de outras pessoas',
        chaves: ['calendario.outros', 'calendario.para_outros'],
        requer: 'calendario.ver',
      },
    ],
  },
  {
    id: 'estatisticas',
    titulo: 'Estatísticas',
    descricao: 'Os números de atendimento e de venda. Cada aba da página tem a sua permissão.',
    telas: ['estatisticas'],
    secoes: [
      {
        titulo: 'Abas que vê',
        chaves: [
          'estatisticas.metas',
          'estatisticas.atendimento',
          'estatisticas.vendas',
          'estatisticas.anuncios',
        ],
        requer: 'estatisticas.ver',
      },
      { titulo: 'Metas', chaves: ['metas.definir'], requer: 'estatisticas.ver' },
    ],
  },
  {
    id: 'configuracao',
    titulo: 'Configuração',
    descricao:
      '"Ver" abre a Configuração só para olhar. Mudar é por parte: cada uma tem a sua permissão.',
    telas: ['configuracao'],
    secoes: [
      {
        titulo: 'O que pode mudar',
        chaves: [
          'configuracao.setores',
          'configuracao.numeros',
          'configuracao.campos',
          'configuracao.funis',
          'configuracao.etiquetas',
          'configuracao.respostas',
          'configuracao.sistema',
        ],
        requer: 'configuracao.ver',
      },
      { titulo: 'WhatsApp', chaves: ['api_oficial'] },
    ],
  },
  {
    id: 'importacao',
    titulo: 'Importar e exportar',
    descricao: 'Dados entrando e saindo do CRM em planilha. Começa desmarcado para todo mundo.',
    telas: [],
    secoes: [
      { titulo: 'Contatos', chaves: ['contatos.importar', 'contatos.exportar'] },
      { titulo: 'Empresas', chaves: ['empresas.importar', 'empresas.exportar'] },
      {
        titulo: 'Outras planilhas',
        chaves: ['estatisticas.exportar'],
      },
      { titulo: 'WhatsApp', chaves: ['historico.importar'] },
    ],
  },
  {
    id: 'dados',
    titulo: 'Acesso aos dados',
    descricao:
      'O que a pessoa consegue enxergar da base. Vale em todas as telas ao mesmo tempo, e por isso tem aba própria.',
    telas: [],
    secoes: [{ titulo: '', chaves: ['ver_telefone', 'ver_base_completa', 'numeros.todos'] }],
  },
  {
    id: 'marketing',
    titulo: 'Marketing',
    descricao: 'O FaroEscolar Marketing: os posts das redes sociais, a aprovação e os perfis.',
    telas: ['marketing'],
    secoes: [
      {
        titulo: 'Aprovação e perfis',
        chaves: ['marketing.aprovar', 'marketing.perfis'],
        requer: 'marketing.ver',
      },
    ],
  },
  {
    id: 'administracao',
    titulo: 'Administração',
    descricao: 'Cuidar da equipe e do sistema, e não do dia a dia.',
    telas: ['logs', 'painel'],
    secoes: [
      {
        titulo: 'Equipe',
        chaves: ['gerenciar_usuarios', 'usuarios.senha', 'supervisao.receber', 'avisos.fora'],
      },
      { titulo: 'Sistema', chaves: ['avisos.ver', 'logs.apagar', 'gerenciar_chatbot'] },
    ],
  },
]

/** Descrição de cada tela, para a grade não depender só do nome. */
export const DESCRICAO_TELA: Record<Tela, string> = {
  bate_papo:
    '"Ver" abre o bate-papo. "Criar" é mandar mensagem de texto ao cliente. "Editar" é organizar o atendimento: chamar apoio, marcar retorno, reabrir conversa encerrada.',
  leads: 'Funil, contatos e empresas. "Criar" cadastra, "Editar" muda o cadastro, "Excluir" apaga.',
  calendario: 'As tarefas e os compromissos. Sozinho, "Ver" mostra as tarefas da própria pessoa.',
  estatisticas: '"Ver" abre a página. Quais abas aparecem fica nas caixas logo abaixo.',
  logs: 'Histórico de quem fez o quê no sistema.',
  configuracao: 'Abre a Configuração para olhar, sem mudar nada.',
  anuncios:
    'Leads que chegaram por anúncio. "Editar" permite qualificar ou desqualificar dentro da conversa.',
  materiais:
    'Fotos, vídeos e PDFs, abertos pelo clipe dentro da conversa. "Editar" é quem monta o acervo: cria pasta, sobe e apaga arquivo.',
  painel:
    'Painel do admin: o que está parado e com quem, como conversa sem resposta, tarefa vencida e negociação parada. "Editar" é dar baixa e limpar o histórico de baixas.',
  marketing:
    'FaroEscolar Marketing. "Ver" abre o calendário de posts, "Criar" monta post novo, "Editar" muda post que ainda não saiu e marca como publicado, "Excluir" apaga.',
}

/** Sugestão aplicada ao criar um usuário novo: um vendedor comum. */
export const PERMISSOES_PADRAO_VENDEDOR: ChavePermissao[] = [
  'bate_papo.ver',
  'bate_papo.criar',
  'bate_papo.editar',
  'bate_papo.fila',
  'bate_papo.todas',
  'bate_papo.iniciar',
  'bate_papo.arquivos',
  'bate_papo.nota_interna',
  'bate_papo.editar_mensagem',
  'bate_papo.apagar_mensagem',
  'leads.ver',
  'leads.criar',
  'leads.editar',
  'leads.mover',
  'leads.fechar',
  'leads.valor',
  'calendario.ver',
  'calendario.criar',
  'calendario.editar',
  'encerrar_chamado',
  'transferir_chamado',
  'agendar_mensagem',
  // Vendedor usa o acervo; montar o acervo é do proprietário.
  'materiais.ver',
  // Quem atende é quem conversa com o lead do anúncio.
  'anuncios.ver',
  'anuncios.editar',
]

/**
 * As abas da página de Estatísticas e a chave de cada uma (ADR 0038). O
 * servidor apaga dos números o que a pessoa não pode ver, e a tela esconde a
 * aba: as duas leem esta lista.
 */
export const ABAS_DAS_ESTATISTICAS = [
  'metas',
  'atendimento',
  'vendas',
  'anuncios',
] as const
export type AbaDasEstatisticas = (typeof ABAS_DAS_ESTATISTICAS)[number]

export const CHAVE_DA_ABA_DAS_ESTATISTICAS: Record<AbaDasEstatisticas, PermissaoEspecial> = {
  metas: 'estatisticas.metas',
  atendimento: 'estatisticas.atendimento',
  vendas: 'estatisticas.vendas',
  anuncios: 'estatisticas.anuncios',
}
